import { createFetch } from '@vueuse/core'
import { defineStore } from 'pinia'

import { API_BASE_URL } from '@/libs/apiBase'
import router from '@/router'
import { useAppStore } from '@/stores/app'

/*
 * The API's base path (`libs/apiBase.ts`). Exported here too because not everything that
 * reaches the API is a fetch: a media file is shown by an <img src> / <video src>,
 * authenticated by the JWT cookie.
 */
export { API_BASE_URL }

export const useFetchStore = defineStore('fetch', () => {
    const appStore = useAppStore()

    const fetch = createFetch({
        baseUrl: API_BASE_URL,
        options: {
            /*
             * The service says why it refused — { error: { code, message } } — and useFetch
             * drops the body of a failed response unless told to keep it. Without this every
             * refusal would read "an error occurred" while the code sat unread.
             */
            updateDataOnError: true,
            beforeFetch({ options }) {
                const headers = new Headers(options.headers)
                const jwt     = appStore.getJWT()

                /*
                 * A caller that states its own credentials keeps them: a session open in the
                 * same browser must not replace them with the person's JWT (the screen-share
                 * page's `Share <secret>`; the screen player once needed this too, and now has
                 * its own fetch, `design/libs/player/api.ts`). A PUBLIC page sends no JWT at
                 * all: what it asks is nobody's session, and a stale token left in this
                 * browser must not decide how the API answers it.
                 */
                const publicPage = router.currentRoute.value.meta.requiresAuth === false

                if (jwt && !publicPage && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${jwt}`)
                options.headers = headers

                return { options }
            },
            async onFetchError(ctx) {
                /*
                 * An expired or revoked session. The login form's own wrong password answers
                 * 401 too, and must stay on the form with its message — so only a session
                 * that exists is thrown away and sent to sign in again. A PUBLIC page is never
                 * sent to the login: its 401 is not the person's session.
                 */
                const current = router.currentRoute.value

                if (ctx.response?.status === 401 && appStore.isLoggedIn() && current.meta.requiresAuth !== false) {
                    appStore.setLogout()
                    await router.push({
                        name:  'login',
                        query: current.name && current.name !== 'login' ? { redirect: current.fullPath } : {},
                    })
                }

                return ctx
            },
        },
    })

    return { fetch }
})
